import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { shipService, Ship } from '../services/shipService';
import { componentService, ComponentItem } from '../services/componentService';
import loadoutService, {
  UserLoadout,
  CreateLoadoutDto,
} from '../services/loadoutService';
import {
  ArrowLeft, Settings, Save, AlertCircle, Search, X, ChevronRight, Rocket, Filter
} from 'lucide-react';
import { PowerManagementPanel, StealthProfilePanel, PowerAllocation } from '../components/power';
import { EmbeddedWeaponList } from '../components/weapons';
import { LoadoutStatsPanel } from '../components/loadout/LoadoutStatsPanel';
import styles from './LoadoutBuilder.module.css';

// ============================================
// MAIN COMPONENT
// ============================================
const LoadoutBuilder: React.FC = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const isAuthenticated = !!token && !!user;
  const [searchParams] = useSearchParams();
  const initialShipId = searchParams.get('shipId');

  // Ship selection
  const [ships, setShips] = useState<Ship[]>([]);
  const [selectedShipId, setSelectedShipId] = useState<number | null>(
    initialShipId ? parseInt(initialShipId) : null
  );
  const [selectedShip, setSelectedShip] = useState<Ship | null>(null);
  
  // Ship search state
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [selectedManufacturer, setSelectedManufacturer] = useState<string | null>(null);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const filterRef = useRef<HTMLDivElement>(null);

  // Loadout state
  const [loadoutName, setLoadoutName] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [savedLoadouts, setSavedLoadouts] = useState<UserLoadout[]>([]);
  const [activeLoadoutId, setActiveLoadoutId] = useState<number | null>(null);
  
  // Component overrides (swapped components)
  const [hardpointOverrides, setHardpointOverrides] = useState<Map<number, ComponentItem>>(new Map());
  // Loaded item overrides (for nested items like salvage heads, mining lasers) - key is "hardpointId:loadedItemId"
  const [loadedItemOverrides, setLoadedItemOverrides] = useState<Map<string, ComponentItem>>(new Map());
  // Power allocations for signature scaling
  const [powerAllocations, setPowerAllocations] = useState<PowerAllocation[]>([]);

  // UI state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load ships
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const shipsResponse = await shipService.getShips({ pageSize: 1000 });
        const loadedShips = shipsResponse.ships || [];
        setShips(loadedShips);
        
        // Preload a default ship if none selected (Aurora MR is a common starter ship)
        if (!initialShipId && loadedShips.length > 0) {
          const defaultShip = loadedShips.find(s => 
            s.name.toLowerCase().includes('aurora') && s.name.toLowerCase().includes('mr')
          ) || loadedShips.find(s => 
            s.name.toLowerCase().includes('gladius')
          ) || loadedShips[0];
          
          if (defaultShip) {
            setSelectedShipId(defaultShip.id);
          }
        }
        
        if (isAuthenticated) {
          try {
            const userLoadouts = await loadoutService.getMyLoadouts();
            setSavedLoadouts(userLoadouts);
          } catch (err) {
            console.warn('Could not load saved loadouts:', err);
          }
        }
        
        setError(null);
      } catch (err) {
        console.error('Failed to load ships:', err);
        setError('Failed to load ship data');
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [isAuthenticated, initialShipId]);

  // Load ship details
  useEffect(() => {
    if (!selectedShipId) {
      setSelectedShip(null);
      return;
    }
    
    const loadShipData = async () => {
      try {
        const shipFromList = ships.find(s => s.id === selectedShipId);
        if (!shipFromList) return;
        const shipData = await shipService.getShipBySlug(shipFromList.slug);
        setSelectedShip(shipData);
        // Reset overrides when ship changes
        setHardpointOverrides(new Map());
        setLoadedItemOverrides(new Map());
      } catch (err) {
        console.error('Error loading ship:', err);
      }
    };
    
    loadShipData();
  }, [selectedShipId, ships]);

  // Handle component swap
  const handleComponentChange = useCallback(async (hardpointId: number, newItemId: number | null) => {
    console.log('📦 LoadoutBuilder handleComponentChange:', { hardpointId, newItemId });
    if (newItemId === null) {
      // Reset to original - remove override
      setHardpointOverrides(prev => {
        const newMap = new Map(prev);
        newMap.delete(hardpointId);
        console.log('✅ Removed override for hardpoint', hardpointId);
        return newMap;
      });
    } else {
      // Fetch the new component and add to overrides
      try {
        console.log('⏳ Fetching component by ID:', newItemId);
        const component = await componentService.getComponentById(newItemId);
        console.log('✅ Component fetched:', component.localizedName);
        setHardpointOverrides(prev => {
          const newMap = new Map(prev);
          newMap.set(hardpointId, component);
          console.log('✅ Override set for hardpoint', hardpointId, '→', component.localizedName);
          console.log('📊 Total overrides:', newMap.size);
          return newMap;
        });
      } catch (err) {
        console.error('❌ Failed to load component:', err);
        console.error('Error details:', err);
      }
    }
  }, []);

  // Handle loaded item swap (for nested items like salvage heads, mining lasers)
  const handleLoadedItemChange = useCallback(async (hardpointId: number, loadedItemId: number, newItemId: number | null) => {
    const key = `${hardpointId}:${loadedItemId}`;
    console.log('📦 LoadoutBuilder handleLoadedItemChange:', { hardpointId, loadedItemId, newItemId, key });
    if (newItemId === null) {
      // Reset to original - remove override
      setLoadedItemOverrides(prev => {
        const newMap = new Map(prev);
        newMap.delete(key);
        console.log('✅ Removed loaded item override for', key);
        return newMap;
      });
    } else {
      // Fetch the new component and add to overrides
      try {
        console.log('⏳ Fetching loaded item component by ID:', newItemId);
        const component = await componentService.getComponentById(newItemId);
        console.log('✅ Loaded item component fetched:', component.localizedName);
        setLoadedItemOverrides(prev => {
          const newMap = new Map(prev);
          newMap.set(key, component);
          console.log('✅ Loaded item override set for', key, '→', component.localizedName);
          console.log('📊 Total loaded item overrides:', newMap.size);
          return newMap;
        });
      } catch (err) {
        console.error('❌ Failed to load loaded item component:', err);
        console.error('Error details:', err);
      }
    }
  }, []);

  // Reset all component overrides to stock defaults
  const handleResetAll = useCallback(() => {
    setHardpointOverrides(new Map());
    setLoadedItemOverrides(new Map());
  }, []);

  // Click outside to close search results and filter popover
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target as Node)) {
        setIsSearchFocused(false);
      }
      if (filterRef.current && !filterRef.current.contains(event.target as Node)) {
        setIsFilterOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Get unique manufacturers from ships
  const manufacturers = useMemo(() => {
    const mfgMap = new Map<string, number>();
    ships.forEach(ship => {
      if (ship.manufacturer) {
        mfgMap.set(ship.manufacturer, (mfgMap.get(ship.manufacturer) || 0) + 1);
      }
    });
    return Array.from(mfgMap.entries())
      .sort((a, b) => b[1] - a[1]) // Sort by count descending
      .map(([name, count]) => ({ name, count }));
  }, [ships]);

  // Filtered ships based on search and manufacturer
  const filteredShips = useMemo(() => {
    let filtered = ships;
    
    // Apply manufacturer filter
    if (selectedManufacturer) {
      filtered = filtered.filter(ship => ship.manufacturer === selectedManufacturer);
    }
    
    // Apply search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(ship => 
        ship.name.toLowerCase().includes(query) ||
        (ship.manufacturer?.toLowerCase().includes(query)) ||
        (ship.focus?.toLowerCase().includes(query))
      );
    }
    
    // No limit when filtering by manufacturer or searching - show all results
    return filtered;
  }, [ships, searchQuery, selectedManufacturer]);

  // Handle ship selection
  const handleSelectShip = (ship: Ship) => {
    setSelectedShipId(ship.id);
    setSearchQuery('');
    setIsSearchFocused(false);
  };

  // Clear ship selection
  const handleClearSelection = () => {
    setSelectedShipId(null);
    setSelectedShip(null);
    setSearchQuery('');
    setSelectedManufacturer(null);
    setTimeout(() => searchInputRef.current?.focus(), 100);
  };

  // Handle manufacturer selection
  const handleManufacturerSelect = (manufacturer: string) => {
    setSelectedManufacturer(selectedManufacturer === manufacturer ? null : manufacturer);
  };

  // Save loadout
  const handleSave = async () => {
    if (!selectedShipId || !loadoutName.trim()) return;
    
    try {
      setSaving(true);
      
      // TODO: Get actual power allocations from PowerManagementPanel when save integration is added
      const loadoutData: CreateLoadoutDto = {
        name: loadoutName.trim(),
        shipId: selectedShipId,
        weaponPips: 2, // Default for now
        shieldPips: 2, // Default for now
        enginePips: 2, // Default for now
        isPublic,
        hardpointOverrides: []
      };
      
      if (activeLoadoutId) {
        await loadoutService.updateLoadout(activeLoadoutId, loadoutData);
      } else {
        const newLoadout = await loadoutService.createLoadout(loadoutData);
        setActiveLoadoutId(newLoadout.id);
        setSavedLoadouts([...savedLoadouts, newLoadout]);
      }
      
      setError(null);
    } catch (err) {
      console.error('Failed to save loadout:', err);
      setError('Failed to save loadout');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <div className={styles.spinner}></div>
          <span>Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <button className={styles.backButton} onClick={() => navigate(-1)}>
          <ArrowLeft size={20} />
          <span>Back</span>
        </button>
        <h1 className={styles.title}>Loadout Builder</h1>
      </div>

      {/* Ship Selector */}
      <div className={styles.shipSelector}>
        {!selectedShip ? (
          /* Search Mode */
          <div className={styles.shipSearch} ref={searchContainerRef}>
            <div className={styles.shipSelectorHeader}>
              <Rocket size={18} className={styles.shipSelectorIcon} />
              <h3>Select Your Ship</h3>
            </div>
            <div className={styles.searchRow}>
              <div className={styles.searchInputWrapper}>
                <Search size={18} className={styles.searchIcon} />
                <input
                  ref={searchInputRef}
                  type="text"
                  className={styles.searchInput}
                  placeholder="Search ships by name, manufacturer, or role..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => setIsSearchFocused(true)}
                />
                {searchQuery && (
                  <button 
                    className={styles.clearSearch}
                    onClick={() => setSearchQuery('')}
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
              
              {/* Filter Button */}
              <div className={styles.filterWrapper} ref={filterRef}>
                <button 
                  className={`${styles.filterButton} ${selectedManufacturer ? styles.filterActive : ''}`}
                  onClick={() => setIsFilterOpen(!isFilterOpen)}
                >
                  <Filter size={16} />
                  <span>{selectedManufacturer || 'Filter'}</span>
                </button>
                
                {/* Filter Popover */}
                {isFilterOpen && (
                  <div className={styles.filterPopover}>
                    <div className={styles.filterPopoverHeader}>
                      <span>Filter by Manufacturer</span>
                      {selectedManufacturer && (
                        <button 
                          className={styles.clearAllFilters}
                          onClick={() => { setSelectedManufacturer(null); setIsFilterOpen(false); }}
                        >
                          Clear
                        </button>
                      )}
                    </div>
                    <div className={styles.filterGrid}>
                      {manufacturers.map(mfg => (
                        <button
                          key={mfg.name}
                          className={`${styles.filterItem} ${selectedManufacturer === mfg.name ? styles.filterItemActive : ''}`}
                          onClick={() => { 
                            setSelectedManufacturer(selectedManufacturer === mfg.name ? null : mfg.name); 
                            setIsFilterOpen(false); 
                          }}
                        >
                          <span className={styles.filterItemName}>{mfg.name}</span>
                          <span className={styles.filterItemCount}>{mfg.count}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {/* Search Results Dropdown */}
            {isSearchFocused && (
              <div className={styles.searchResults}>
                
                {/* Ship Results */}
                <div className={styles.shipResultsList}>
                  {filteredShips.map(ship => (
                    <button
                      key={ship.id}
                      className={styles.shipResult}
                      onClick={() => handleSelectShip(ship)}
                    >
                      <img 
                        src={ship.thumbnailUrl || ship.storeImageUrl || '/images/ship-art-placeholder.png'}
                        alt={ship.name}
                        className={styles.shipResultImage}
                        onError={(e) => { (e.target as HTMLImageElement).src = '/images/ship-art-placeholder.png'; }}
                      />
                      <div className={styles.shipResultInfo}>
                        <span className={styles.shipResultName}>{ship.name}</span>
                        <span className={styles.shipResultMeta}>
                          {ship.manufacturer} • {ship.size}
                        </span>
                      </div>
                    </button>
                  ))}
                  {filteredShips.length === 0 && searchQuery && (
                    <div className={styles.noResults}>
                      No ships found matching "{searchQuery}"
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        ) : (
          /* Selected Ship Display */
          <div className={styles.selectedShipBar}>
            <img 
              src={selectedShip.thumbnailUrl || selectedShip.storeImageUrl || '/images/ship-art-placeholder.png'}
              alt={selectedShip.name}
              className={styles.selectedShipImage}
              onError={(e) => { (e.target as HTMLImageElement).src = '/images/ship-art-placeholder.png'; }}
            />
            <div className={styles.selectedShipInfo}>
              <span className={styles.selectedShipName}>
                {selectedShip.manufacturer} {selectedShip.name}
              </span>
              <span className={styles.selectedShipMeta}>
                {selectedShip.size} • {selectedShip.focus || selectedShip.role}
              </span>
            </div>
            <button 
              className={styles.changeShipBtn}
              onClick={handleClearSelection}
            >
              Change Ship
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className={styles.error}>
          <AlertCircle size={24} />
          <span>{error}</span>
        </div>
      )}

      {!selectedShip && !error && (
        <div className={styles.placeholder}>
          <Settings size={64} className={styles.placeholderIcon} />
          <h2>Select a ship to configure power distribution</h2>
          <p>The Engineering system allows you to allocate power pips across 7 categories.</p>
        </div>
      )}

      {selectedShip && (
        <div className={styles.mainLayout}>
          {/* Main Content Area */}
          <div className={styles.mainContent}>
            {/* Power Management Panel - with embedded weapon selection */}
            {selectedShip.hardpoints && (
              <PowerManagementPanel
                ship={selectedShip}
                hardpoints={selectedShip.hardpoints}
                readOnly={false}
                onComponentChange={handleComponentChange}
                hardpointOverrides={hardpointOverrides}
                onLoadedItemChange={handleLoadedItemChange}
                loadedItemOverrides={loadedItemOverrides}
                onResetAll={handleResetAll}
                onPowerAllocationsChange={setPowerAllocations}
                weaponContent={
                  <EmbeddedWeaponList
                    ship={selectedShip}
                    hardpoints={selectedShip.hardpoints}
                    readOnly={false}
                    onComponentChange={handleComponentChange}
                    onLoadedItemChange={handleLoadedItemChange}
                    hardpointOverrides={hardpointOverrides}
                    loadedItemOverrides={loadedItemOverrides}
                    onResetWeapons={handleResetAll}
                  />
                }
              />
            )}

            {/* Stealth Profile Panel - Signature & detectability (reactive to component swaps) */}
            {selectedShip.hardpoints && (
              <StealthProfilePanel
                ship={selectedShip}
                hardpoints={selectedShip.hardpoints}
                hardpointOverrides={hardpointOverrides}
              />
            )}

            {/* Save Section */}
            {isAuthenticated && (
              <div className={styles.saveSection}>
                <h3><Save size={20} /> Save Loadout</h3>
                <div className={styles.saveForm}>
                  <input
                    type="text"
                    placeholder="Loadout name..."
                    value={loadoutName}
                  onChange={(e) => setLoadoutName(e.target.value)}
                  className={styles.loadoutNameInput}
                />
                <label className={styles.publicToggle}>
                  <input
                    type="checkbox"
                    checked={isPublic}
                    onChange={(e) => setIsPublic(e.target.checked)}
                  />
                  <span>Public</span>
                </label>
                <button 
                  onClick={handleSave}
                  disabled={saving || !loadoutName.trim()}
                  className={styles.saveButton}
                >
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          )}
          </div>
          
          {/* Stats Sidebar */}
          {selectedShip.hardpoints && (
            <LoadoutStatsPanel
              ship={selectedShip}
              hardpoints={selectedShip.hardpoints}
              hardpointOverrides={hardpointOverrides}
              loadedItemOverrides={loadedItemOverrides}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default LoadoutBuilder;
